import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import kycService from "@/services/api/kyc.service";
import { Loader } from "lucide-react";

const KycStatusPage = () => {
  const navigate = useNavigate();
  const [kycStatus, setKycStatus] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkKycStatus = async () => {
    try {
      setLoading(true);
      const response = await kycService.checkKycStatus();
      if (response.status === "success") {
        setKycStatus(response.data);
      } else {
        setError("Failed to verify KYC status");
      }
    } catch (err) {
      console.error("Error checking KYC status:", err);
      setError("Failed to verify your KYC status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkKycStatus();
  }, []);

  const handleCompleteKyc = () => {
    navigate("/profile/kyc");
  };

  const renderStatusCard = () => {
    if (!kycStatus) return null;

    const {
      isKycComplete,
      kycStatus: backendStatus,
      missingItems,
      rejectionReason,
      completionPercentage,
      submittedDate,
      reviewedDate,
    } = kycStatus;
    let status;
    switch (backendStatus) {
      case "verified":
        status = "verified";
        break;
      case "pending":
        status = "pending";
        break;
      case "in-review": // Add this case
        status = "in_review"; // Or keep as "in-review"
        break;
      case "rejected":
        status = "rejected";
        break;
      case "draft": // 🔥 Add this
        status = "draft";
        break;
      case "not_started":
      case null:
      case undefined:
        status = "not_started";
        break;
      default:
        status = "unknown";
    }
    if (loading) {
      return (
        <div className="flex justify-center items-center p-8">
          <Loader className="animate-spin text-indigo-500 mb-4" size={36} />
        </div>
      );
    }

    if (isKycComplete) {
      return (
        <div className="bg-white rounded-lg shadow-md p-8 max-w-2xl mx-auto text-center ">
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <svg
                className="w-8 h-8 text-green-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              KYC Verified
            </h2>
            <p className="text-gray-600 mb-4">
              Your KYC verification is complete. All features are now available.
            </p>
            {reviewedDate && (
              <p className="text-sm text-gray-500">
                Verified on: {new Date(reviewedDate).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>
      );
    }

    let statusClass = "";
    let statusText = "";
    let statusIcon = null;
    let statusDescription = "";
    let showActionButton = true;
    let actionLabel = "Complete KYC";

    switch (status) {
      case "not_started":
        statusClass = "bg-yellow-50 border-yellow-200 text-yellow-800";
        statusText = "Not Started";
        statusDescription =
          "Please complete KYC verification to access all features.";
        actionLabel = "Start KYC";
        statusIcon = (
          <svg
            className="w-8 h-8 text-yellow-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        );
        break;
      case "pending":
        statusClass = "bg-blue-50 border-blue-200 text-blue-800";
        statusText = "Pending Review";
        if (completionPercentage === 100) {
          statusDescription =
            "Your KYC verification is pending approval. Some features may be limited until verification is complete.";
          showActionButton = false;
        } else {
          statusDescription = `Your KYC verification is ${completionPercentage}% complete.`;
          if (missingItems?.length > 0) {
            statusDescription += ` Missing: ${missingItems.join(", ")}.`;
            actionLabel = "Complete Missing Details";
          }
        }
        statusIcon = (
          <svg
            className="w-8 h-8 text-blue-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        );
        break;

      case "in_review":
        statusClass = "bg-purple-50 border-purple-200 text-purple-800";
        statusText = "In Review";
        statusDescription =
          "Your KYC is currently under review. Please wait for verification.";
        showActionButton = false;
        statusIcon = (
          <svg
            className="w-8 h-8 text-purple-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        );
        break;
      case "rejected":
        statusClass = "bg-red-50 border-red-200 text-red-800";
        statusText = "Rejected";
        statusDescription = `Your KYC was rejected. Reason: ${
          rejectionReason || "Unknown reason"
        }. Please update and resubmit.`;
        actionLabel = "Update KYC";
        statusIcon = (
          <svg
            className="w-8 h-8 text-red-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        );
        break;
      case "draft":
        statusClass = "bg-orange-50 border-orange-200 text-orange-800";
        statusText = "Draft";
        statusDescription = `Your KYC is ${completionPercentage}% complete.`;
        if (missingItems?.length > 0) {
          statusDescription += ` Missing: ${missingItems.join(", ")}.`;
          actionLabel = "Continue KYC";
        }
        statusIcon = (
          <svg
            className="w-8 h-8 text-orange-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        );
        break;

      default:
        statusClass = "bg-gray-50 border-gray-200 text-gray-800";
        statusText = "Unknown Status";
        statusDescription = "KYC verification status could not be determined.";
        showActionButton = false;
    }

    return (
      <div
        className={`border rounded-lg shadow-md mt-16 p-8 max-w-2xl mx-auto ${statusClass}`}
      >
        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center mb-4 shadow-sm">
            {statusIcon}
          </div>
          <h2 className="text-2xl font-bold mb-2">KYC Status: {statusText}</h2>
          <p className="mb-6 max-w-md">{statusDescription}</p>

          {completionPercentage !== undefined && completionPercentage < 100 && (
            <div className="w-full max-w-xs mb-6">
              <div className="flex justify-between text-sm mb-1">
                <span>Completion Progress</span>
                <span>{completionPercentage}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-500"
                  style={{ width: `${completionPercentage}%` }}
                ></div>
              </div>
            </div>
          )}

          {showActionButton && (
            <button
              onClick={handleCompleteKyc}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {actionLabel}
            </button>
          )}

          {submittedDate && (
            <p className="text-sm text-gray-500 mt-4">
              Submitted on: {new Date(submittedDate).toLocaleDateString()}
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className=" flex items-center justify-center p-4 mt-8 bg-gray-50">
      <div className="w-full">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg mb-6 max-w-2xl mx-auto">
            {error}
          </div>
        )}
        {renderStatusCard()}
      </div>
    </div>
  );
};

export default KycStatusPage;
